import { Dropdown, MenuProps } from 'antd'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Button, Chevron, NavigationModal, Icon, Edit } from '@lmc/ui-kit'
import { useState } from 'react'
import dayjs from 'dayjs'
import useRotaRemove from 'screens/Rostering/hooks/useRotaRemove'
import {
  ISO_STRING_FORMAT,
  getDateWithoutUTC,
} from 'screens/Rostering/utils.datetime'
import { DAILY, WEEKLY } from 'screens/Rostering/constants'
import { Link, useNavigate } from 'react-router-dom'

type RotaBuilderDropdownProps = {
  currentDate: dayjs.Dayjs
  isDailyView: boolean
  onViewChange: (event: any) => void
  isZoomed?: boolean
  handleZoom?: () => void
  publishedRota?: boolean
  showAllEvents?: boolean
  onToggleShowAllEvents?: () => void
}

const RotaBuilderDropdown = ({
  currentDate,
  isDailyView,
  onViewChange,
  isZoomed,
  handleZoom,
  publishedRota,
  showAllEvents,
  onToggleShowAllEvents,
}: RotaBuilderDropdownProps) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const [showPrompt, setShowPrompt] = useState<boolean>(false)

  const { removeRota } = useRotaRemove()

  const getMenuProps = () => {
    const items: MenuProps['items'] = [
      {
        label: isDailyView
          ? t('SWITCH_TO_WEEKLY_VIEW')
          : t('SWITCH_TO_DAILY_VIEW'),
        key: 'switch-view',
        onClick: () => {
          onViewChange({ target: { value: isDailyView ? WEEKLY : DAILY } })
        },
      },
      isDailyView &&
        handleZoom && {
          label: t(isZoomed ? 'ZOOM_OUT' : 'ZOOM_IN'),
          key: 'zoom',
          onClick: handleZoom,
        },
      onToggleShowAllEvents && {
        label: t(showAllEvents ? 'SHOW_COLLAPSED_VIEW' : 'SHOW_ALL_EVENTS'),
        key: 'toggle-events-view',
        onClick: onToggleShowAllEvents,
      },
      {
        type: 'divider',
      },
      {
        label: t('RESET_ROTA'),
        key: 'reset-rota',
        onClick: () => {
          setShowPrompt(true)
        },
      },
    ].filter(Boolean) as MenuProps['items']

    return { items }
  }

  const handleConfirmNavigation = () => {
    removeRota?.({ date: getDateWithoutUTC(currentDate, ISO_STRING_FORMAT) })

    setShowPrompt(false)
  }

  return publishedRota ? (
    <Button>
      <StyledLink
        to={{
          pathname: '/rota/builder',
          search: window.location.search,
        }}
      >
        <Edit />
        {t('BUILDER')}
      </StyledLink>
    </Button>
  ) : (
    <>
      <Dropdown menu={getMenuProps()} trigger={['hover']}>
        <span>
          <StyledButton variant="text">
            {t('OPTIONS')} <Chevron direction="down" />
          </StyledButton>
        </span>
      </Dropdown>
      <NavigationModal
        modalTitle={t('RESET_ROTA_MODAL_TITLE')}
        modalContentText={t('RESET_ROTA_MODAL_DESCRIPTION')}
        modalConfirmText={t('RESET_ROTA')}
        modalCancelText={t('CANCEL')}
        showPrompt={showPrompt}
        confirmNavigation={handleConfirmNavigation}
        cancelNavigation={() => setShowPrompt(false)}
      />
    </>
  )
}

const StyledButton = styled(Button)`
  color: ${(props) => props.theme.color.neutral.color3};
  &:hover {
    color: ${(props) => props.theme.color.hover.primary};
  }
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  width: 100%;
  padding: 0;
`

export default RotaBuilderDropdown
